<template>
  <div class="reqs">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ t('requests.inboxTitle') }}</h1>
        <p class="page-sub">{{ t('requests.inboxSub') }}</p>
      </div>
    </div>

    <div v-if="loading" class="spinner"></div>
    <div v-else-if="!pending.length && !awaiting.length && !reviewed.length" class="empty-state"><p>{{ t('requests.inboxEmpty') }}</p></div>

    <template v-else>
      <div v-if="pending.length" class="req-cards">
        <div v-for="r in pending" :key="r.id" class="req-card">
          <div class="rc-head">
            <span class="req-status st-pending">{{ t('requests.pending') }}</span>
            <span class="rc-type">{{ r.type === 'project' ? t('requests.typeProject') : t('requests.typeTask') }}</span>
          </div>
          <div class="rc-title">{{ r.title }}</div>
          <div class="rc-from">{{ t('requests.from') }} <strong>{{ r.client_name }}</strong></div>
          <p v-if="r.description" class="rc-desc">{{ r.description }}</p>
          <div class="rc-terms">
            <span v-if="r.desired_deadline">📅 {{ fmtDate(r.desired_deadline) }}</span>
            <span v-if="r.desired_pricing_type !== 'none'">{{ pricingLabel(r) }}</span>
          </div>
          <div class="rc-actions">
            <button class="btn btn-sm btn-ghost danger-text" :disabled="busy === r.id" @click="decline(r)">{{ t('requests.decline') }}</button>
            <button class="btn btn-sm btn-ghost" :disabled="busy === r.id" @click="openRevise(r)">{{ t('requests.revise') }}</button>
            <button class="btn btn-sm btn-primary" :disabled="busy === r.id" @click="approve(r)">{{ t('requests.approve') }}</button>
          </div>
        </div>
      </div>

      <template v-if="awaiting.length">
        <div class="section-header"><h2 class="section-title">{{ t('requests.awaitingClient') }}</h2></div>
        <div class="req-list">
          <div v-for="r in awaiting" :key="r.id" class="req-item">
            <div class="req-main">
              <div class="req-title">{{ r.title }}</div>
              <div class="req-meta">{{ r.client_name }} · {{ t('requests.revisedSent') }}</div>
            </div>
            <span class="req-status st-revised">{{ t('requests.revised') }}</span>
          </div>
        </div>
      </template>

      <template v-if="reviewed.length">
        <div class="section-header"><h2 class="section-title">{{ t('requests.reviewed') }}</h2></div>
        <div class="req-list">
          <div v-for="r in reviewed" :key="r.id" class="req-item">
            <div class="req-main">
              <div class="req-title">{{ r.title }}</div>
              <div class="req-meta">{{ r.client_name }} · {{ r.type === 'project' ? t('requests.typeProject') : t('requests.typeTask') }}</div>
            </div>
            <span :class="['req-status', 'st-' + r.status]">{{ t('requests.' + r.status) }}</span>
          </div>
        </div>
      </template>
    </template>

    <!-- Revise modal -->
    <div v-if="reviseTarget" class="modal-backdrop" @click.self="reviseTarget = null">
      <div class="modal">
        <div class="modal-header">
          <h2>{{ t('requests.reviseTitle') }}</h2>
          <button class="btn btn-icon modal-close" @click="reviseTarget = null">✕</button>
        </div>
        <div class="form-group"><label>{{ t('requests.fieldTitle') }}</label><input v-model="rv.title" class="form-control" maxlength="200" /></div>
        <div class="form-group"><label>{{ t('requests.fieldDesc') }}</label><textarea v-model="rv.description" class="form-control" rows="3"></textarea></div>
        <div class="form-group"><label>{{ t('requests.deadline') }}</label><input v-model="rv.desired_deadline" type="date" class="form-control" /></div>
        <div class="form-group">
          <label>{{ t('requests.billingPref') }}</label>
          <select v-model="rv.desired_pricing_type" class="form-control">
            <option value="none">{{ t('projects.pricingNone') }}</option>
            <option value="fixed">{{ t('projects.pricingFixed') }}</option>
            <option value="hourly">{{ t('projects.pricingHourly') }}</option>
          </select>
          <input v-if="rv.desired_pricing_type !== 'none'" v-model="rv.desired_price_amount" type="number" min="0" step="0.01" class="form-control" style="margin-top:8px" :placeholder="t('requests.amount')" />
        </div>
        <div class="modal-actions">
          <button class="btn btn-ghost" @click="reviseTarget = null">{{ t('common.cancel') }}</button>
          <button class="btn btn-primary" :disabled="saving" @click="submitRevise">{{ saving ? t('common.saving') : t('requests.sendRevision') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import api from '@/api'
import { useI18n } from '@/composables/useI18n'
import { onRealtime, debounce } from '@/composables/useRealtime'
import { fmtMoney } from '@/utils/billing'

export default {
  name: 'RequestsView',
  setup() {
    const { t, locale } = useI18n()
    const all = ref([])
    const loading = ref(true)
    const busy = ref(null)
    const reviseTarget = ref(null)
    const saving = ref(false)
    const rv = reactive({ title: '', description: '', desired_deadline: '', desired_pricing_type: 'none', desired_price_amount: '' })

    const pending  = computed(() => all.value.filter(r => r.status === 'pending'))
    const awaiting = computed(() => all.value.filter(r => r.status === 'revised'))
    const reviewed = computed(() => all.value.filter(r => r.status === 'approved' || r.status === 'declined'))

    function fmtDate(dt) {
      if (!dt) return ''
      return new Date(dt + 'T00:00:00').toLocaleDateString(locale.value === 'he' ? 'he-IL' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    }
    const pricingLabel = (r) => {
      const lbl = r.desired_pricing_type === 'fixed' ? t('projects.pricingFixed') : t('projects.pricingHourly')
      return r.desired_price_amount != null ? `${lbl} ${fmtMoney(r.desired_price_amount)}` : lbl
    }

    async function load() {
      loading.value = true
      try { all.value = (await api.getRequests()).data || [] } catch {} finally { loading.value = false }
    }
    async function approve(r) {
      busy.value = r.id
      try { await api.approveRequest(r.id); await load() } catch {} finally { busy.value = null }
    }
    async function decline(r) {
      if (!confirm(t('requests.declineConfirm', { title: r.title }))) return
      busy.value = r.id
      try { await api.declineRequest(r.id); await load() } catch {} finally { busy.value = null }
    }
    function openRevise(r) {
      reviseTarget.value = r
      Object.assign(rv, {
        title: r.title, description: r.description || '', desired_deadline: r.desired_deadline || '',
        desired_pricing_type: r.desired_pricing_type, desired_price_amount: r.desired_price_amount ?? '',
      })
    }
    async function submitRevise() {
      saving.value = true
      try {
        await api.reviseRequest(reviseTarget.value.id, {
          title: rv.title,
          description: rv.description || null,
          desired_deadline: rv.desired_deadline || null,
          desired_pricing_type: rv.desired_pricing_type,
          desired_price_amount: rv.desired_pricing_type !== 'none' && rv.desired_price_amount !== '' ? Number(rv.desired_price_amount) : null,
        })
        reviseTarget.value = null
        await load()
      } catch {} finally { saving.value = false }
    }

    let off = () => {}
    onMounted(() => { load(); off = onRealtime('request.changed', debounce(load, 400)) })
    onUnmounted(() => off())

    return { t, pending, awaiting, reviewed, loading, busy, approve, decline, openRevise, reviseTarget, rv, saving, submitRevise, pricingLabel, fmtDate }
  },
}
</script>

<style scoped>
.page-sub { color: var(--muted); font-size: 13px; margin-top: 2px; }
.req-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 12px; margin-bottom: 8px; }
.req-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 16px; }
.rc-head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
.rc-type { font-size: 11px; color: var(--muted); text-transform: uppercase; letter-spacing: .04em; }
.rc-title { font-size: 15px; font-weight: 600; }
.rc-from { font-size: 12px; color: var(--muted); margin-top: 3px; }
.rc-desc { font-size: 13px; line-height: 1.5; margin-top: 10px; white-space: pre-wrap; }
.rc-terms { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.rc-terms span { font-size: 12px; background: var(--accent-wash); border-radius: 8px; padding: 4px 10px; }
.rc-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 14px; flex-wrap: wrap; }
.danger-text { color: var(--danger); }

.section-header { margin: 26px 0 14px; }
.section-title { font-size: 16px; font-weight: 600; }
.req-list { display: flex; flex-direction: column; gap: 8px; }
.req-item { display: flex; align-items: center; gap: 12px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 14px 16px; }
.req-main { flex: 1; min-width: 0; }
.req-title { font-size: 14px; font-weight: 500; }
.req-meta { font-size: 12px; color: var(--muted); margin-top: 2px; }
.req-status { font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 999px; white-space: nowrap; }
.st-pending  { background: var(--accent-wash); color: var(--accent); }
.st-revised  { background: rgba(180,117,20,.14); color: var(--high); }
.st-approved { background: rgba(30,138,83,.12); color: var(--success); }
.st-declined { background: rgba(209,68,68,.12); color: var(--danger); }

.modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.6); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
.modal { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; width: 100%; max-width: 460px; padding: 24px; box-shadow: var(--shadow); }
.modal-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 18px; }
.modal-header h2 { font-size: 18px; font-weight: 700; }
.modal-close { color: var(--muted); background: none; border: none; cursor: pointer; font-size: 16px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 18px; }
</style>
